/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useState } from 'react';
import { useFetcher } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { resourceRoutes } from '@/route-paths';
import { SEARCH_PREVIEW_MIN_LENGTH, type SearchPreviewResult } from '@/lib/search/types';

export interface UseSearchPreviewResult {
    /** Latest preview for the current query; undefined while the query is shorter than the minimum length. */
    data?: SearchPreviewResult;
    /** True from the moment the query changes (including the debounce wait) until its preview has loaded. */
    isLoading: boolean;
}

/** How long a preview is reused; matches the 60s TTL that the `availability` expansion gives the response. */
const CACHE_TTL_MS = 60_000;
/** Most recent terms kept; the oldest entry is evicted beyond this. */
const CACHE_MAX_ENTRIES = 30;

/**
 * Previews already loaded in this visit, by normalized term. Module-level so it survives the search bar remounting on
 * every navigation (the header keys it by location).
 */
const previewCache = new Map<string, { data: SearchPreviewResult; storedAt: number }>();

const cacheKey = (term: string) => term.trim().toLowerCase();

function getCachedPreview(term: string): SearchPreviewResult | undefined {
    const entry = previewCache.get(cacheKey(term));
    if (!entry) return undefined;
    if (Date.now() - entry.storedAt > CACHE_TTL_MS) {
        previewCache.delete(cacheKey(term));
        return undefined;
    }
    return entry.data;
}

function cachePreview(data: SearchPreviewResult): void {
    const key = cacheKey(data.query);
    previewCache.delete(key);
    previewCache.set(key, { data, storedAt: Date.now() });
    if (previewCache.size > CACHE_MAX_ENTRIES) {
        const oldest = previewCache.keys().next().value;
        if (oldest !== undefined) previewCache.delete(oldest);
    }
}

/** Empties the preview cache. Exposed for tests. */
export function clearSearchPreviewCache(): void {
    previewCache.clear();
}

function isSearchPreviewResult(value: unknown): value is SearchPreviewResult {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as SearchPreviewResult).query === 'string' &&
        Array.isArray((value as SearchPreviewResult).products) &&
        Array.isArray((value as SearchPreviewResult).suggestions)
    );
}

/**
 * Interaction-driven data for the header search dropdown: once the query reaches
 * {@link SEARCH_PREVIEW_MIN_LENGTH} characters, loads `/resource/search-preview` (debounced by
 * `pages.search.suggestionsDebounce`) and reloads it as the query changes. Previews are cached per term for
 * {@link CACHE_TTL_MS}, so returning to a term searched moments ago (e.g. after a backspace) makes no request.
 */
export function useSearchPreview(query: string): UseSearchPreviewResult {
    const fetcher = useFetcher();
    const debounceMs = useConfig().pages.search.suggestionsDebounce;
    const term = query.trim();
    const enabled = term.length >= SEARCH_PREVIEW_MIN_LENGTH;
    // Term of the latest request; differs from `term` while the debounce for a newer query is pending.
    const [requestedTerm, setRequestedTerm] = useState<string>();

    const fetched = isSearchPreviewResult(fetcher.data) ? fetcher.data : undefined;
    useEffect(() => {
        if (fetched) cachePreview(fetched);
    }, [fetched]);

    const cached = enabled ? getCachedPreview(term) : undefined;
    const hasCached = cached !== undefined;

    useEffect(() => {
        if (!enabled || hasCached) return;
        const timeout = setTimeout(() => {
            setRequestedTerm(term);
            void fetcher.load(`${resourceRoutes.searchPreview}?q=${encodeURIComponent(term)}`);
        }, debounceMs);
        return () => clearTimeout(timeout);
    }, [term, enabled, hasCached, debounceMs]); // oxlint-disable-line react-hooks/exhaustive-deps -- fetcher.load is stable per React Router

    // The fetched preview is the current one once it answers this term; otherwise it is the previous term's preview,
    // kept on screen (dimmed) while the new one loads.
    const isCurrent = fetched !== undefined && cacheKey(fetched.query) === cacheKey(term);

    return {
        data: enabled ? (cached ?? fetched) : undefined,
        isLoading:
            enabled &&
            !hasCached &&
            !isCurrent &&
            (requestedTerm !== term || fetcher.state !== 'idle'),
    };
}
